import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { inventoryService } from "../../services/inventoryService";
import Icon from "../../components/Common/Icon";
import AdvancedTable from "../../components/Common/AdvancedTable";

const fmtMoney = (v) =>
  v != null ? `${Number(v).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺` : "—";
const fmtQty = (v) =>
  v != null ? Number(v).toLocaleString("tr-TR", { maximumFractionDigits: 2 }) : "0";
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("tr-TR") : "—");

const iso = (d) => d.toISOString().slice(0, 10);
const monthStart = () => {
  const d = new Date();
  return iso(new Date(d.getFullYear(), d.getMonth(), 1));
};

const TABS = [
  { key: "sales", label: "Satış Performansı", icon: "trending_up" },
  { key: "service", label: "Hizmet Tüketimi", icon: "content_cut" },
  { key: "cost", label: "Stok Maliyet", icon: "payments" },
];

export default function StockReportsPage() {
  const [tab, setTab] = useState("sales");
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(iso(new Date()));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sales, setSales] = useState(null);
  const [service, setService] = useState(null);
  const [cost, setCost] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = { from, to };
    try {
      if (tab === "sales") setSales((await inventoryService.salesPerformance(params)).data.data);
      else if (tab === "service")
        setService((await inventoryService.serviceConsumption(params)).data.data);
      else setCost((await inventoryService.stockCost(params)).data.data);
    } catch (err) {
      setError(err.response?.data?.error ?? "Rapor yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, [tab, from, to]);

  useEffect(() => {
    load();
  }, [load]);

  const inputCls =
    "border border-outline-variant rounded-xl px-3 py-2 outline-none focus:border-primary text-sm bg-white";

  return (
    <div className="space-y-6">
      {/* Başlık */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link
            to="/inventory"
            className="w-11 h-11 rounded-xl bg-surface-container-high text-on-surface-variant flex items-center justify-center hover:bg-surface-container-highest transition"
            title="Stok Yönetimine dön"
          >
            <Icon name="arrow_back" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-on-surface">Stok Analizi</h1>
            <p className="text-on-surface-variant text-sm">
              Satış performansı, hizmet başına tüketim ve stok maliyet analizi.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inputCls} />
          <span className="text-on-surface-variant text-sm">—</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inputCls} />
        </div>
      </div>

      {/* Sekmeler */}
      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`rounded-xl px-4 py-2 font-semibold text-sm border transition flex items-center gap-1.5 ${
              tab === t.key
                ? "bg-primary text-white border-primary"
                : "bg-white text-on-surface-variant border-outline-variant hover:bg-surface-container-high"
            }`}
          >
            <Icon name={t.icon} className="!text-[18px]" />
            {t.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-error bg-error/10 border border-error/20 rounded-xl px-4 py-3">
          <Icon name="error" className="!text-[18px]" /> {error}
        </div>
      )}

      {loading ? (
        <div className="bg-surface-card rounded-3xl p-8 border border-outline-variant text-center text-on-surface-variant">
          Yükleniyor…
        </div>
      ) : (
        <>
          {tab === "sales" && <SalesReport data={sales} />}
          {tab === "service" && <ServiceReport data={service} />}
          {tab === "cost" && <CostReport data={cost} />}
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, tone = "primary" }) {
  return (
    <div className="bg-surface-card rounded-2xl p-4 border border-outline-variant">
      <div className="text-xs text-on-surface-variant font-semibold">{label}</div>
      <div className={`text-xl font-bold mt-1 text-${tone}`}>{value}</div>
    </div>
  );
}

const SectionTitle = ({ icon, children }) => (
  <h2 className="text-sm font-bold text-on-surface flex items-center gap-2 mt-2">
    <Icon name={icon} className="!text-[18px] text-primary" />
    {children}
  </h2>
);

// --- Satış Performansı ---
function SalesReport({ data }) {
  if (!data) return null;
  const rows = (data.rows ?? []).map((r) => ({ ...r, id: r.productId }));
  const details = (data.details ?? []).map((d, i) => ({ ...d, id: i }));

  const productCols = [
    { key: "productName", header: "Ürün", type: "string" },
    {
      key: "quantity",
      header: "Miktar",
      type: "number",
      render: (v, row) => (
        <span>
          {fmtQty(v)} <span className="text-[11px] text-on-surface-variant">{row.unit}</span>
        </span>
      ),
    },
    { key: "revenue", header: "Ciro", type: "number", render: (v) => <span className="text-success">{fmtMoney(v)}</span> },
    { key: "cost", header: "Maliyet", type: "number", render: (v) => fmtMoney(v) },
    {
      key: "profit",
      header: "Kâr",
      type: "number",
      render: (v) => <span className="font-semibold">{fmtMoney(v)}</span>,
    },
  ];

  const detailCols = [
    { key: "date", header: "Tarih", type: "date", render: (v) => fmtDate(v) },
    { key: "productName", header: "Ürün", type: "string" },
    {
      key: "customerName",
      header: "Müşteri (Kime Satıldı)",
      type: "string",
      render: (v) =>
        v ? (
          <span className="font-medium text-on-surface">{v}</span>
        ) : (
          <span className="text-on-surface-variant italic">Perakende / belirtilmemiş</span>
        ),
    },
    {
      key: "quantity",
      header: "Miktar",
      type: "number",
      render: (v, row) => (
        <span>
          {fmtQty(v)} <span className="text-[11px] text-on-surface-variant">{row.unit}</span>
        </span>
      ),
    },
    { key: "unitPrice", header: "Birim Fiyat", type: "number", render: (v) => fmtMoney(v) },
    { key: "revenue", header: "Ciro", type: "number", render: (v) => <span className="text-success">{fmtMoney(v)}</span> },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Satılan Miktar" value={fmtQty(data.totalQuantity)} />
        <StatCard label="Ciro" value={fmtMoney(data.totalRevenue)} tone="success" />
        <StatCard label="Maliyet" value={fmtMoney(data.totalCost)} tone="error" />
        <StatCard label="Kâr" value={fmtMoney(data.totalProfit)} tone="primary" />
      </div>

      <SectionTitle icon="leaderboard">Ürün Bazında (en çok / en az satan)</SectionTitle>
      <AdvancedTable columns={productCols} data={rows} searchPlaceholder="Ürün ara..." />

      <SectionTitle icon="receipt_long">Satış Detayı — kime, ne zaman</SectionTitle>
      <AdvancedTable columns={detailCols} data={details} searchPlaceholder="Ürün veya müşteri ara..." />
    </div>
  );
}

// --- Hizmet Tüketimi ---
function ServiceReport({ data }) {
  if (!data) return null;
  // Nested (hizmet → ürün) yapıyı akıllı tablo için düzleştir.
  const rows = [];
  (data.services ?? []).forEach((s, si) => {
    (s.products ?? []).forEach((p, pi) => {
      rows.push({
        id: `${si}-${pi}`,
        serviceName: s.serviceName ?? "Hizmet belirtilmemiş",
        productName: p.productName,
        unit: p.unit,
        quantity: p.quantity,
        cost: p.cost,
      });
    });
  });

  const cols = [
    { key: "serviceName", header: "Hizmet", type: "string" },
    { key: "productName", header: "Tüketilen Malzeme", type: "string" },
    {
      key: "quantity",
      header: "Miktar",
      type: "number",
      render: (v, row) => (
        <span>
          {fmtQty(v)} <span className="text-[11px] text-on-surface-variant">{row.unit}</span>
        </span>
      ),
    },
    { key: "cost", header: "Maliyet", type: "number", render: (v) => <span className="text-error">{fmtMoney(v)}</span> },
  ];

  return (
    <div className="space-y-4">
      <StatCard label="Toplam Tüketim Maliyeti" value={fmtMoney(data.totalCost)} tone="error" />
      <SectionTitle icon="content_cut">Hizmet Başına Tüketilen Malzeme</SectionTitle>
      <AdvancedTable columns={cols} data={rows} searchPlaceholder="Hizmet veya malzeme ara..." />
    </div>
  );
}

// --- Stok Maliyet ---
function CostReport({ data }) {
  if (!data) return null;
  const valuation = (data.valuation ?? []).map((p) => ({ ...p, id: p.productId }));

  const cols = [
    { key: "productName", header: "Ürün", type: "string" },
    {
      key: "quantity",
      header: "Stok",
      type: "number",
      render: (v, row) => (
        <span>
          {fmtQty(v)} <span className="text-[11px] text-on-surface-variant">{row.unit}</span>
        </span>
      ),
    },
    { key: "costPrice", header: "Birim Maliyet", type: "number", render: (v) => fmtMoney(v) },
    {
      key: "stockValue",
      header: "Stok Değeri",
      type: "number",
      render: (v) => <span className="font-semibold">{fmtMoney(v)}</span>,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Anlık Stok Değeri" value={fmtMoney(data.currentStockValue)} tone="primary" />
        <StatCard label="Dönem Alım" value={fmtMoney(data.purchaseCost)} />
        <StatCard label="Tüketim Maliyeti" value={fmtMoney(data.consumptionCost)} tone="error" />
        <StatCard label="Fire" value={fmtMoney(data.wasteCost)} tone="error" />
        <StatCard label="Satış Cirosu" value={fmtMoney(data.salesRevenue)} tone="success" />
        <StatCard label="Satış Maliyeti" value={fmtMoney(data.salesCost)} />
        <StatCard label="Satış Kârı" value={fmtMoney(data.salesProfit)} tone="success" />
      </div>

      <SectionTitle icon="inventory_2">Ürün Bazında Anlık Stok Değeri</SectionTitle>
      <AdvancedTable columns={cols} data={valuation} searchPlaceholder="Ürün ara..." />
    </div>
  );
}
